import { computed, onMounted, ref } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import {
  DEFAULT_CHOICE_KEY,
  EnrollmentStatus,
  isChoiceKey,
  type ChoiceKey,
  type NupStatusResponse,
} from '@ark/nup-shared';
import { NUP_ACTIVITY_ENDED_VAR } from '../constants/fego-variables';
import { apiClient } from '../../shared/api';
import { getCopy } from '../constants/copy';
import { findAwardedImages, findMissedImage } from '../constants/rewards';
import { useNupLang } from './use-nup-lang';
import { usePageTypeFlag } from './use-page-type';
import {
  DEFAULT_DEPOSIT_URL,
  DEFAULT_PRIZE_URL,
  toLink,
  toLogin,
} from '../utils/link';
import {
  getEnrollBlockToast,
  shouldShowEnrollBtn,
} from '../utils/enroll-guard';
import { showToast, toastApiError } from '../utils/toast';

export interface NupPageLinks {
  rulesUrl?: string;
  depositUrl?: string;
  prizeUrl?: string;
}

export function useNupPage(getLinks: () => NupPageLinks) {
  const { useVariable } = fegoUse;
  const fegoVars = useVariable() as Record<string, string>;
  const { isPC } = usePageTypeFlag();
  const { isEn } = useNupLang();
  const copy = computed(() => getCopy(isEn.value));
  const status = ref<NupStatusResponse | null>(null);
  const loading = ref(true);
  const submitting = ref(false);
  const successVisible = ref(false);
  const choiceVisible = ref(false);
  const choiceSuccessVisible = ref(false);
  const pendingChoiceKey = ref<ChoiceKey>(DEFAULT_CHOICE_KEY);

  const enrollment = computed(() => {
    return status.value && status.value.enrollment
      ? status.value.enrollment
      : null;
  });

  const choiceKey = computed<ChoiceKey | null>(() => {
    return enrollment.value ? enrollment.value.choiceKey : null;
  });

  const isInProgress = computed(() => {
    const row = enrollment.value;
    return !!(
      row &&
      (row.status === EnrollmentStatus.IN_PROGRESS ||
        row.status === EnrollmentStatus.SETTLING)
    );
  });

  const isAwarded = computed(() => {
    return !!(
      enrollment.value && enrollment.value.status === EnrollmentStatus.AWARDED
    );
  });

  const isBelowThreshold = computed(() => {
    return !!(
      enrollment.value &&
      enrollment.value.status === EnrollmentStatus.BELOW_THRESHOLD
    );
  });

  const depositEnded = computed(() => {
    const row = enrollment.value;
    return !!(row && Date.now() >= row.depositEndTime);
  });

  const inDepositWindow = computed(
    () => isInProgress.value && !depositEnded.value,
  );

  const showCountdown = computed(() => inDepositWindow.value);
  const showChoiceBtn = computed(() => {
    return inDepositWindow.value && !choiceKey.value;
  });
  const showDepositBtn = computed(() => inDepositWindow.value);
  const showAwarded = computed(() => isAwarded.value);
  const showMissed = computed(() => isBelowThreshold.value);
  const showTierList = computed(
    () => !isAwarded.value && !isBelowThreshold.value,
  );

  const awardedImages = computed(() => {
    if (isBelowThreshold.value) {
      return [findMissedImage(isPC.value, isEn.value)];
    }
    const row = enrollment.value;
    return findAwardedImages(
      row ? row.netDepositAmount : null,
      choiceKey.value || DEFAULT_CHOICE_KEY,
      isPC.value,
      isEn.value,
    );
  });

  /** 仅未报名或 status=3 再出示报名；已发奖 / 未达标 / 进行中隐藏。 */
  const showEnrollBtn = computed(() => {
    return shouldShowEnrollBtn(
      loading.value,
      !!status.value,
      enrollment.value ? enrollment.value.status : null,
    );
  });

  const depositEndTime = computed(() => {
    return enrollment.value ? enrollment.value.depositEndTime : null;
  });

  const netDepositAmount = computed(() => {
    return enrollment.value ? enrollment.value.netDepositAmount : null;
  });

  const stickyText = computed(() => {
    if (showDepositBtn.value) {
      return copy.value.depositNow;
    }
    return copy.value.enrollNow;
  });

  const showSticky = computed(
    () => showEnrollBtn.value || showDepositBtn.value,
  );

  async function refreshStatus(): Promise<void> {
    try {
      status.value = await apiClient.getStatus();
      fegoVars[NUP_ACTIVITY_ENDED_VAR] = status.value.activityEnded ? '1' : '0';
    } catch (error) {
      toastApiError(error, copy.value.loadFailed, isEn.value);
    } finally {
      loading.value = false;
    }
  }

  async function handleEnroll(): Promise<void> {
    if (submitting.value) {
      return;
    }
    const current = status.value;
    if (!current) {
      return;
    }
    if (!current.loggedIn) {
      toLogin();
      return;
    }
    const blockToast = getEnrollBlockToast(current, isEn.value);
    if (blockToast) {
      showToast(blockToast);
      return;
    }
    submitting.value = true;
    try {
      const next = await apiClient.enroll();
      status.value = {
        ...current,
        canEnroll: false,
        enrollment: next,
      };
      successVisible.value = true;
    } catch (error) {
      toastApiError(error, undefined, isEn.value);
    } finally {
      submitting.value = false;
    }
  }

  async function handleUpdateChoice(nextKey: ChoiceKey): Promise<boolean> {
    if (submitting.value || !isChoiceKey(nextKey)) {
      return false;
    }
    submitting.value = true;
    try {
      const result = await apiClient.updateChoice(nextKey);
      if (status.value && status.value.enrollment) {
        status.value = {
          ...status.value,
          enrollment: {
            ...status.value.enrollment,
            choiceKey: result.choiceKey,
          },
        };
      }
      choiceVisible.value = false;
      choiceSuccessVisible.value = true;
      return true;
    } catch (error) {
      toastApiError(error, undefined, isEn.value);
      return false;
    } finally {
      submitting.value = false;
    }
  }

  function handleDeposit(): void {
    const links = getLinks();
    toLink(links.depositUrl || DEFAULT_DEPOSIT_URL);
  }

  function handleRules(): void {
    const url = getLinks().rulesUrl;
    if (!url) {
      showToast(copy.value.rulesSoon);
      return;
    }
    toLink(url);
  }

  function handleViewPrize(): void {
    toLink(getLinks().prizeUrl || DEFAULT_PRIZE_URL);
  }

  function handleStickyClick(): void {
    if (showDepositBtn.value) {
      handleDeposit();
      return;
    }
    handleEnroll();
  }

  function openChoice(nextKey?: string): void {
    pendingChoiceKey.value =
      nextKey && isChoiceKey(nextKey) ? nextKey : DEFAULT_CHOICE_KEY;
    choiceVisible.value = true;
  }

  function closeSuccess(): void {
    successVisible.value = false;
  }

  function closeChoice(): void {
    choiceVisible.value = false;
  }

  function closeChoiceSuccess(): void {
    choiceSuccessVisible.value = false;
  }

  onMounted(() => {
    refreshStatus();
  });

  return {
    loading,
    submitting,
    status,
    enrollment,
    choiceKey,
    showCountdown,
    showChoiceBtn,
    showDepositBtn,
    showEnrollBtn,
    showAwarded,
    showMissed,
    showTierList,
    awardedImages,
    showSticky,
    stickyText,
    depositEndTime,
    netDepositAmount,
    isInProgress,
    successVisible,
    choiceVisible,
    choiceSuccessVisible,
    pendingChoiceKey,
    handleEnroll,
    handleUpdateChoice,
    handleDeposit,
    handleRules,
    handleViewPrize,
    handleStickyClick,
    openChoice,
    closeSuccess,
    closeChoice,
    closeChoiceSuccess,
  };
}
